import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DASHBOARD } from "@/constants/testIds";
import DailyMissionCard from "@/components/DailyMissionCard";
import {
  Flame, Sparkles, Award, MessageSquare, ArrowRight, BookOpen, Target, CalendarClock, Star,
  Trophy, TrendingUp, Play, Clock, Rocket, Zap, Timer, Milestone, Feather, Building2,
} from "lucide-react";

const DAY_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function Skeleton({ className = "" }) {
  return <div className={`rounded-lg bg-secondary/60 animate-pulse ${className}`} />;
}

function JourneyIcon({ kind, achieved }) {
  const cls = `h-3.5 w-3.5 ${achieved ? "text-primary" : "text-muted-foreground/40"}`;
  const map = { start: Rocket, lesson: BookOpen, quiz: Zap, cert: Trophy, streak: Flame, goal: Target };
  const Icon = map[kind] || Milestone;
  return <Icon className={cls} />;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [assigned, setAssigned] = useState({ slugs: new Set(), companyName: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/me/dashboard-cards").catch(() => ({ data: null })),
      api.get("/me/study-schedule").catch(() => ({ data: null })),
      api.get("/me/weekly-reflection").catch(() => ({ data: null })),
      api.get("/companies/mine/assigned-paths").catch(() => ({ data: null })),
    ]).then(([c, s, r, a]) => {
      setCards(c.data); setSchedule(s.data); setReflection(r.data);
      if (a?.data) setAssigned({
        slugs: new Set(a.data.assigned_slugs || []),
        companyName: a.data.company_name,
      });
    }).finally(() => setLoading(false));
  }, []);

  const goal = cards?.goal_projection || {};
  const mentorStats = cards?.mentor_sessions || {};
  const certs = cards?.next_certificate || {};
  const journey = cards?.journey_timeline || [];
  const insights = cards?.insights || [];
  const greeting = cards?.greeting || {};

  return (
    <div data-testid={DASHBOARD.root} className="space-y-8 animate-fade-up">
      {/* Enterprise banner — visible when the user's workspace has assigned mandatory paths */}
      {assigned.slugs.size > 0 && (
        <Link
          to="/app/paths"
          data-testid="dashboard-enterprise-banner"
          className="group flex items-center gap-4 rounded-2xl border border-accent/50 bg-gradient-to-r from-accent/15 via-accent/5 to-transparent p-4 transition hover:border-accent hover:shadow-[0_0_28px_-8px_rgba(204,255,0,0.4)]"
        >
          <div className="grid h-10 w-10 place-items-center rounded-lg border border-accent/50 bg-accent/15 text-accent">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mono text-accent">// assigned by {assigned.companyName || "your workspace"}</div>
            <div className="mt-1 font-display font-bold">
              {assigned.slugs.size} mandatory path{assigned.slugs.size !== 1 ? "s" : ""} waiting for you
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-accent group-hover:translate-x-1 transition" />
        </Link>
      )}

      {/* Dynamic greeting */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-primary">// dashboard</div>
          <h1 data-testid={DASHBOARD.welcome} className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">
            {loading ? <Skeleton className="h-10 w-96" /> : (greeting.salutation || `Hi ${(user?.name||"Learner").split(" ")[0]}, ready to learn?`)}
          </h1>
          {!loading && greeting.line && (
            <p className="mt-1 text-sm text-muted-foreground">{greeting.line}</p>
          )}
        </div>
        <Button
          data-testid={DASHBOARD.quickAskBtn}
          className="rounded-full btn-neon font-mono text-xs uppercase tracking-[0.2em]"
          onClick={() => navigate("/app/tutor")}
        >
          <Sparkles className="mr-2 h-4 w-4" /> Talk to your Mentor
        </Button>
      </div>

      {/* Daily Mission */}
      <DailyMissionCard />

      {/* Insights strip */}
      {insights.length > 0 && (
        <div data-testid="dashboard-insights" className="flex flex-wrap gap-2">
          {insights.map((s, i) => (
            <div key={i} className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 font-mono text-[0.7rem] text-primary">
              <Sparkles className="h-3 w-3" /> {s}
            </div>
          ))}
        </div>
      )}

      {/* Premium widget row: Goal · Streak · Mentor Sessions · Certificates */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Goal projection */}
        <Link
          to="/app/notebook"
          data-testid="dashboard-goal-widget"
          className="card-hover rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-5 group transition hover:border-primary hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.35)]"
        >
          <div className="flex items-center justify-between">
            <div className="label-mono">Goal</div>
            <Target className="h-4 w-4 text-primary" />
          </div>
          {loading ? <Skeleton className="mt-3 h-14 w-full" /> : (
            <>
              <div className="mt-2 font-display text-base font-black tracking-tight line-clamp-2 min-h-[2.5em]">
                {goal.target || "Set your goal"}
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${goal.progress_percent || 0}%` }} />
              </div>
              <div className="mt-1 flex items-center justify-between label-mono">
                <span>{goal.progress_percent || 0}%</span>
                {goal.progress_percent >= 100
                  ? <span className="text-accent font-bold">Complete ✓</span>
                  : goal.ai_confidence && <span className="text-primary">{goal.ai_confidence} conf.</span>}
              </div>
              {(goal.estimated_completion || goal.current_milestone) && (
                <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs">
                  {goal.estimated_completion && (
                    <div className="flex items-center gap-1.5 text-muted-foreground"><Timer className="h-3 w-3" /> ETA · <span className="text-foreground">{goal.estimated_completion}</span></div>
                  )}
                  {goal.current_milestone && (
                    <div className="flex items-center gap-1.5 text-muted-foreground"><Milestone className="h-3 w-3" /> next · <span className="text-foreground truncate">{goal.current_milestone}</span></div>
                  )}
                </div>
              )}
            </>
          )}
        </Link>

        {/* Streak */}
        <div data-testid={DASHBOARD.streakCard} className="card-hover rounded-2xl border border-border bg-card p-5 transition hover:border-accent/50">
          <div className="flex items-center justify-between">
            <div className="label-mono">Current streak</div>
            <Flame className="h-4 w-4 text-accent" />
          </div>
          <div className="mt-3 flex items-end gap-2">
            <div className="font-display text-4xl font-black tracking-tighter">{user?.streak ?? 0}</div>
            <div className="text-sm text-muted-foreground pb-2">days</div>
          </div>
          <div className="mt-3 flex gap-1">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition ${i < ((user?.streak ?? 0) % 7 || 0) ? "bg-accent" : "bg-secondary"}`} />
            ))}
          </div>
        </div>

        {/* Mentor Sessions */}
        <Link
          to="/app/tutor"
          data-testid="dashboard-mentor-widget"
          className="card-hover rounded-2xl border border-border bg-card p-5 transition hover:border-primary/50"
        >
          <div className="flex items-center justify-between">
            <div className="label-mono">Mentor sessions</div>
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          {loading ? <Skeleton className="mt-3 h-14 w-full" /> : (
            <>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="font-display text-3xl font-black tracking-tighter">{mentorStats.total || 0}</div>
                <div className="text-xs text-muted-foreground">sessions</div>
              </div>
              <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3 w-3" /> {Math.floor((mentorStats.total_minutes || 0) / 60)}h {(mentorStats.total_minutes || 0) % 60}m learning
                </div>
                {mentorStats.last_human && (
                  <div className="text-muted-foreground">last · <span className="text-foreground">{mentorStats.last_human}</span></div>
                )}
                <div className="text-muted-foreground">avg · <span className="text-foreground">{mentorStats.avg_minutes || 0} min</span></div>
              </div>
            </>
          )}
        </Link>

        {/* Next certificate */}
        <Link
          to={certs.next ? certs.next.url : "/app/certificates"}
          data-testid="dashboard-cert-widget"
          className="card-hover rounded-2xl border border-border bg-card p-5 transition hover:border-accent/50"
        >
          <div className="flex items-center justify-between">
            <div className="label-mono">Certificates</div>
            <Award className="h-4 w-4 text-accent" />
          </div>
          {loading ? <Skeleton className="mt-3 h-14 w-full" /> : (
            <>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-3xl font-black tracking-tighter">{certs.earned || 0}</div>
                <div className="text-xs text-muted-foreground">earned</div>
              </div>
              {certs.next ? (
                <div className="mt-3 space-y-1 border-t border-border pt-3">
                  <div className="label-mono">next · <span className="text-foreground truncate">{certs.next.path_title}</span></div>
                  <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${certs.next.percent}%` }} />
                  </div>
                  <div className="label-mono">{certs.next.percent}% · {certs.next.lessons_remaining} lesson{certs.next.lessons_remaining !== 1 ? "s" : ""} left</div>
                </div>
              ) : (
                <div className="mt-3 label-mono">Enroll in a path to unlock a certificate.</div>
              )}
            </>
          )}
        </Link>
      </div>

      {/* Weekly schedule + journey timeline */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div data-testid="dashboard-week-schedule" className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /><h2 className="font-display text-xl font-bold">This week</h2></div>
            <Link to="/app/planner" className="text-xs font-mono text-primary hover:underline">Edit planner →</Link>
          </div>
          {schedule?.planner?.days?.length ? (
            <>
              <div className="grid grid-cols-7 gap-2">
                {DAY_SHORT.map((d, i) => {
                  const isStudy = schedule.planner.days.includes(i);
                  const count = (schedule.week_activity || {})[i] ?? (schedule.week_activity || {})[String(i)] ?? 0;
                  return (
                    <div key={i} className={`rounded-lg border p-2 text-center transition ${isStudy ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                      <div className="label-mono">{d}</div>
                      <div className={`mt-1 text-lg font-black tracking-tighter ${count > 0 ? "text-accent" : isStudy ? "text-primary" : "text-muted-foreground"}`}>{count > 0 ? count : "·"}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 label-mono">
                {schedule.today_is_study_day
                  ? <span className="text-accent">today · {schedule.planner.preferred_time} · {schedule.planner.duration_minutes} min</span>
                  : "today is a rest day."}
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-6 text-center">
              <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground" />
              <div className="mt-2 text-sm text-muted-foreground">No study planner set yet.</div>
              <Button className="mt-4 rounded-full btn-neon font-mono text-xs uppercase tracking-widest" onClick={() => navigate("/app/planner")}>Set my planner</Button>
            </div>
          )}
        </div>

        {/* Journey timeline */}
        <div data-testid="dashboard-journey" className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2"><Milestone className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">Your journey</h2></div>
          {loading ? <Skeleton className="h-64 w-full" /> : (
            <ol className="relative border-l border-border pl-4 space-y-3">
              {journey.map((j, i) => (
                <li key={i} className="relative">
                  <span className={`absolute -left-[21px] top-1 grid h-4 w-4 place-items-center rounded-full border ${j.achieved ? "border-primary bg-primary/20" : "border-border bg-background"}`}>
                    <JourneyIcon kind={j.icon} achieved={j.achieved} />
                  </span>
                  <div className={`text-sm font-medium ${j.achieved ? "text-foreground" : "text-muted-foreground/60"}`}>{j.label}</div>
                  {j.ts ? <div className="label-mono">{new Date(j.ts).toLocaleDateString()}</div>
                        : j.achieved && <div className="label-mono text-accent">achieved</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Weekly reflection — the personal letter */}
      {reflection?.reflection?.letter && (
        <div data-testid="dashboard-weekly-reflection" className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-6">
          <div className="mb-3 flex items-center gap-2"><Feather className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-bold">This week in your journey</h2></div>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">{reflection.reflection.letter}</div>
          {reflection.reflection.next_week_recommendation && (
            <div className="mt-4 rounded-lg border border-accent/40 bg-accent/5 p-3">
              <div className="label-mono text-accent">// next week</div>
              <p className="mt-1 text-sm">{reflection.reflection.next_week_recommendation}</p>
            </div>
          )}
        </div>
      )}

      {/* Badges */}
      {user?.badges?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="label-mono mb-3">// badges earned</div>
          <div className="flex flex-wrap gap-2">
            {user.badges.map((b) => (
              <span key={b} className="rounded-full border border-accent/40 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">🏆 {b.replace(/_/g, " ")}</span>
            ))}
          </div>
        </div>
      )}

      {/* Continue learning */}
      <div className="rounded-2xl border border-border bg-card p-6" data-testid={DASHBOARD.currentCourses}>
        <div className="mb-5 flex items-center justify-between">
          <div>
            <div className="label-mono">// continue learning</div>
            <h2 className="mt-1 font-display text-xl font-bold">Current courses</h2>
          </div>
          <Link to="/app/paths" className="text-xs font-mono text-primary hover:underline">Browse all →</Link>
        </div>
        {cards?.next_lesson ? (
          <Link
            to={cards.next_lesson.url}
            className="group flex items-center gap-4 rounded-xl border border-primary/30 bg-primary/5 p-4 transition hover:border-primary hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.4)]"
          >
            <div className="grid h-11 w-11 place-items-center rounded-lg border border-primary/40 bg-primary/10 text-primary">
              <Play className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="label-mono text-primary">next up · {cards.next_lesson.path}</div>
                {(() => {
                  const m = /\/app\/paths\/([^/]+)/.exec(cards.next_lesson.url || "");
                  const slug = m && m[1];
                  return slug && assigned.slugs.has(slug) ? (
                    <span className="rounded-full border border-accent bg-accent/20 px-2 py-0.5 font-mono text-[0.55rem] uppercase tracking-widest text-accent">Mandatory</span>
                  ) : null;
                })()}
              </div>
              <div className="mt-1 font-display font-bold truncate">{cards.next_lesson.title}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-1 transition" />
          </Link>
        ) : (
          <div className="rounded-xl border border-dashed border-border p-6 text-center">
            <BookOpen className="mx-auto h-8 w-8 text-muted-foreground" />
            <div className="mt-2 text-sm text-muted-foreground">All caught up — pick a new path.</div>
            <Button className="mt-4 rounded-full btn-neon font-mono text-xs uppercase tracking-widest" onClick={() => navigate("/app/paths")}>Explore paths</Button>
          </div>
        )}
      </div>
    </div>
  );
}
